import { useContext, useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import moment from 'moment';
import { t } from "i18next";
import trainingIcon from "../../assets/images/trainings.svg";
import { Link } from "react-router-dom";
import esLocale from '@fullcalendar/core/locales/es';
import { LoadingSpinner } from "../general/loading-spinner";
import { UserContext } from "../../App";
import { getCoachPlanning } from "../../services/planning";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { CoachTrainingResults } from "../pages/coach/training/training-results";

export function CoachMiniCalendarBlock() {
    const { userProfile } = useContext(UserContext);

    const [events, setEvents] = useState<any[]>([]);
    const [planning, setPlanning] = useState<any[]>([]);
    const [selectedPlanningEvent, setSelectedPlanningEvent] = useState<any>();
    const [showPlanningResults, setShowPlanningResults] = useState(false);
    const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
    const [currentMonth, setCurrentMonth] = useState(moment());
    const [selectedTeamId, setSelectedTeamId] = useState<string>('');
    const isLoadingRef = useRef(false);

    const renderTooltip = (props: any) => (
        <Tooltip class="event-tooltip" {...props}>
            <div className="time">
                { props.event._def.extendedProps.day_moment == 'morning' ? t("Morning session") : t("Afternoon session") }
            </div>
            <div className="tooltip-content">
                <div className="team-name">{ props.event._def.extendedProps.team_name}{ props.event._def.extendedProps.club_name ? ` (${props.event._def.extendedProps.club_name})` : '' }</div>
                <div className="event-title">{ props.event.title }</div>
            </div>
            <div className="actions">
                <a className="btn btn-primary" data-team={ props.event._def.extendedProps.team_id }
                    data-day={ props.event._def.extendedProps.day } data-moment={ props.event._def.extendedProps.day_moment }
                    data-session={ props.event._def.extendedProps.day_moment_session } onClick={ handleShowResults }>
                    { t("Results") }
                </a>
                <Link to={ '/coach/training/monitoring/' + props.event._def.extendedProps.team_id + '/' + props.event._def.extendedProps.day + '/' + props.event._def.extendedProps.day_moment + '/' + props.event._def.extendedProps.day_moment_session } className="btn btn-primary" data-day={ props.event._def.publicId }>
                    { t("Monitoring") }
                </Link>
            </div>
        </Tooltip>
    )

    const handleShowResults = (event: any) => {
        event.preventDefault();
        const index = planning.findIndex((p: any) => p.team_id == event.currentTarget.dataset.team && p.datetime == event.currentTarget.dataset.day &&
            p.day_moment == event.currentTarget.dataset.moment && p.day_moment_session == event.currentTarget.dataset.session);
        if (index !== -1) {
            setSelectedPlanningEvent(planning[index]);
            setShowPlanningResults(true);
        } else {
            setSelectedPlanningEvent(Object.assign({}));
        }
        
    }

    function eventContent(arg: any) {
        return (
            <OverlayTrigger placement="top" delay={{ show: 250, hide: 1000 }} overlay={renderTooltip(arg)}>
                <span className={ arg.event.classNames } /*href="#" onClick={ handleEventClick} title={ arg.event.title}*/></span>
            </OverlayTrigger>);
    }

    // Función para cargar eventos de un rango de fechas específico
    const loadEventsForMonth = (month: moment.Moment) => {
        // Evitar llamadas duplicadas
        if (isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        const startDate = month.clone().startOf('month').format('YYYY-MM-DD');
        const endDate = month.clone().endOf('month').format('YYYY-MM-DD');

        getCoachPlanning(userProfile.reference_id, startDate, endDate)
            .then(response => {
                setPlanning(response.data);

                const mappedEvents = response.data.map((e: any) => {
                    return {
                        id: e.datetime + '-' + e.day_moment + '-' + e.day_moment_session + '-' + e.team_id,
                        title: e.description ?? 'Sesión de entrenamiento (' + t(e.day_moment) + ')',
                        date: moment(e.datetime).format('YYYY-MM-DD'),
                        time: moment(e.datetime).format('HH:mm:ss'),
                        when: e.when,
                        coach: userProfile.reference_id,
                        season_id: e.season_id,
                        team_id: e.team_id,
                        team_name: e.team_name,
                        club_name: e.club_name || '',
                        day: moment(e.datetime).format('YYYY-MM-DD'),
                        day_moment: e.day_moment,
                        day_moment_session: e.day_moment_session,
                        color: e.color
                    }
                });
                setEvents(mappedEvents);
                if (selectedTeamId) {
                    setFilteredEvents(mappedEvents.filter((e: any) => String(e.team_id) === selectedTeamId));
                } else {
                    setFilteredEvents(mappedEvents);
                }
                isLoadingRef.current = false;
            })
            .catch(error => {
                console.error('Error loading coach planning:', error);
                isLoadingRef.current = false;
            });
    };

    useEffect(() => {
        loadEventsForMonth(currentMonth);
    }, []);

    const handlePlanningResultsOnClose = () => {
        setSelectedPlanningEvent(Object.assign({}));
        setShowPlanningResults(false);
    }

    // Derive unique teams from events
    const teams = events.reduce((acc: { id: number; name: string; club_name: string }[], e: any) => {
        if (!acc.find(t => t.id === e.team_id)) {
            acc.push({ id: e.team_id, name: e.team_name, club_name: e.club_name || '' });
        }
        return acc;
    }, []).sort((a: any, b: any) => a.name.localeCompare(b.name));

    const handleTeamFilterChange = (teamId: string) => {
        setSelectedTeamId(teamId);
        if (teamId) {
            setFilteredEvents(events.filter((e: any) => String(e.team_id) === teamId));
        } else {
            setFilteredEvents(events);
        }
    };

    const handleDatesSet = (dateInfo: any) => {
        // Detectar el mes principal que se está mostrando
        const viewDate = moment(dateInfo.start);
        const viewEnd = moment(dateInfo.end);
        const middleDate = moment(viewDate).add(Math.floor(viewEnd.diff(viewDate, 'days') / 2), 'days');
        const newMonth = middleDate.clone().startOf('month');

        // Solo cargar eventos si el mes cambió
        if (!newMonth.isSame(currentMonth, 'month')) {
            setCurrentMonth(newMonth);
            loadEventsForMonth(newMonth);
        }
    }

    return (
        <div className="block mini-calendar-container">
            <div className="block-inner">
                <h3 className="block-title">
                    <img src={ trainingIcon } alt={ t("trainings")} />
                    { t("trainings") }
                </h3>
                <div className="block-content">
                    <Form.Select
                        size="sm"
                        className="mb-2"
                        value={selectedTeamId}
                        onChange={(e) => handleTeamFilterChange(e.target.value)}
                    >
                        <option value="">{t("All teams")}</option>
                        {teams.map((team: any) => (
                            <option key={team.id} value={team.id}>
                                {team.club_name ? `${team.name} (${team.club_name})` : team.name}
                            </option>
                        ))}
                    </Form.Select>
                    <FullCalendar
                        plugins={[ dayGridPlugin, bootstrap5Plugin ]}
                        initialView="dayGridMonth"
                        weekends={true}
                        firstDay={ 1 }
                        locale={esLocale}
                        height={ 'auto' }
                        events={ filteredEvents }
                        eventContent= { eventContent }
                        datesSet={ handleDatesSet }
                    />
                    <div className="events-list">
                        {
                            filteredEvents.map((e: any) => {
                                return <div key={ 'event-' + e.id } className="event-item">
                                        <div className="color-mark" style={{backgroundColor: e.color}}></div>
                                        <div className="name">
                                            <Link to={ '/coach/planning/session/' + e.season_id + '/' + e.team_id + '/' + e.date + '/' + e.day_moment + '/' + e.day_moment_session } title={ e.title }>
                                                { e.title }
                                            </Link>
                                        </div>
                                        <div className="team">{ e.team_name }{ e.club_name ? ` (${e.club_name})` : '' }</div>
                                        <div className="date">
                                            { moment(e.id, 'YYYY-MM-DD HH:mm:ss').format('D MMMM') + ' (' + (e.day_moment == 'morning' ? t("Morning session") : t("Afternoon session")) + ')' }
                                        </div>
                                    </div>
                            })
                        }
                    </div>
                </div>
            </div>
            { showPlanningResults && selectedPlanningEvent &&
                <CoachTrainingResults event={ selectedPlanningEvent } onClose={ handlePlanningResultsOnClose } />
            }
        </div>
    );
}  
